import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Header from "../Header";
import Footer from "../Footer";
import useSeo from "../../hooks/useSeo";
import { formatPrice, productImage, shopGet, shopPost } from "../../api/shopApi";

export default function ShopOrderDetailPage() {
  const { orderNo } = useParams();
  const navigate = useNavigate();
  const userId = Number(localStorage.getItem("user_id"));
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useSeo({
    title: order ? `订单 ${order.order_no}` : "订单详情",
    path: `/shop/orders/${orderNo}`,
    noindex: true,
  });

  const load = () => {
    if (!userId || !orderNo) return;
    shopGet(`/orders/${encodeURIComponent(orderNo)}?user_id=${userId}`)
      .then(setOrder)
      .catch((e) => setError(e.message || "订单不存在"));
  };

  useEffect(() => {
    if (!userId) {
      navigate(`/login?redirect=${encodeURIComponent(`/shop/orders/${orderNo}`)}`, { replace: true });
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, orderNo, navigate]);

  const cancel = async () => {
    if (!window.confirm("确认取消该订单？")) return;
    setBusy(true);
    try {
      const data = await shopPost(`/orders/${encodeURIComponent(orderNo)}/cancel`, {
        user_id: userId,
        reason: "用户取消",
      });
      setOrder(data.order || data);
    } catch (e) {
      setError(e.message || "取消失败");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-amber-50 flex flex-col">
      <Header />
      <main className="flex-1 px-4 py-8">
        <div className="max-w-2xl mx-auto space-y-4">
          <Link to="/shop/orders" className="text-sm text-pink-600 hover:underline">
            ← 我的订单
          </Link>
          {error && !order ? (
            <p className="text-center text-rose-500 py-12">{error}</p>
          ) : !order ? (
            <p className="text-center text-gray-400 py-12">加载中…</p>
          ) : (
            <div className="bg-white rounded-3xl shadow p-6 space-y-5">
              <div className="flex justify-between gap-3">
                <div>
                  <h1 className="text-xl font-bold text-gray-800">订单详情</h1>
                  <p className="text-sm text-gray-500 mt-1">编号 {order.order_no}</p>
                </div>
                <span className="text-pink-600 font-semibold">{order.status_label}</span>
              </div>
              <p className="text-xs text-gray-400">
                下单时间：{order.created_at ? new Date(order.created_at).toLocaleString() : "-"}
              </p>
              <div className="space-y-3 border-t pt-4">
                {(order.items || []).map((it) => (
                  <div key={it.id || it.product_id} className="flex gap-3">
                    <img
                      src={productImage(it.product_image)}
                      alt={it.product_name}
                      className="w-16 h-16 rounded-lg object-cover"
                    />
                    <div className="flex-1 text-sm">
                      <p className="font-medium text-gray-800">{it.product_name}</p>
                      <p className="text-gray-500 mt-1">
                        {formatPrice(it.unit_price)} × {it.quantity}
                      </p>
                    </div>
                    <p className="text-sm font-semibold text-rose-600">{formatPrice(it.line_total)}</p>
                  </div>
                ))}
              </div>
              <div className="border-t pt-4 text-sm text-gray-600 space-y-1">
                <p>商品金额：{formatPrice(order.subtotal)}</p>
                <p>运费：{formatPrice(order.shipping_fee)}</p>
                <p className="text-base font-bold text-gray-800">
                  合计：{formatPrice(order.total_amount)}
                </p>
              </div>
              <div className="border-t pt-4 text-sm text-gray-600 space-y-1">
                <p className="font-semibold text-gray-800">收货信息</p>
                <p>
                  {order.recipient_name} {order.recipient_phone}
                </p>
                <p>{order.shipping_address}</p>
                {order.customer_note ? <p>备注：{order.customer_note}</p> : null}
              </div>
              {(order.tracking_company || order.tracking_no) && (
                <div className="border-t pt-4 text-sm text-gray-600 space-y-1">
                  <p className="font-semibold text-gray-800">物流信息</p>
                  <p>
                    {order.tracking_company || "物流"} {order.tracking_no || ""}
                  </p>
                </div>
              )}
              {order.public_admin_note ? (
                <div className="rounded-xl bg-rose-50 p-3 text-sm text-gray-700">
                  平台说明：{order.public_admin_note}
                </div>
              ) : null}
              {error ? <p className="text-rose-500 text-sm">{error}</p> : null}
              {order.can_cancel ? (
                <button
                  type="button"
                  disabled={busy}
                  onClick={cancel}
                  className="w-full py-3 rounded-full border border-rose-300 text-rose-600 disabled:opacity-50"
                >
                  {busy ? "处理中…" : "取消订单"}
                </button>
              ) : null}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
